import { defineStore } from 'pinia'
import axios from '@/api/axios'
import { useSwal } from '@/composables'
import { extractClapError } from '@/helpers/errorHelper'
import { useCurrentGameStore } from './game/currentGame'
import { useQuestionStore } from './question/question'

export interface MediaGalleryConf {
  hideButton: boolean
  onlyImage?: boolean
  type?: string
  current?: string
}

interface State {
  count: number
  mediaStore: Media[]
  perPage: number
  mediaGallery: {
    open: boolean
    conf: MediaGalleryConf
  }
}

const MARK_SRC_MEDIA_TRANSFORMATION =
  '/upload/if_w_ne_1200_or_h_ne_850/w_1200,h_850,c_fill/if_end/'

function normalizeMarkSrcMedia(media: Media): Media {
  const url = media?.url || ''

  if (!url.includes('/upload/') || url.includes('if_w_ne_1200_or_h_ne_850')) {
    return media
  }

  return {
    ...media,
    url: url.replace('/upload/', MARK_SRC_MEDIA_TRANSFORMATION),
  }
}

export const useMediaStore = defineStore('media', {
  state: (): State => ({
    count: 0,
    mediaStore: [],
    perPage: 6,
    mediaGallery: {
      open: false,
      conf: {
        hideButton: true,
        onlyImage: false,
        type: '',
      },
    },
  }),

  getters: {
    isHideButton: state => state.mediaGallery.conf.hideButton,
    isOnlyImage: state => state.mediaGallery.conf.onlyImage,
    type: state => state.mediaGallery?.conf?.type,
    current: state => state.mediaGallery.conf?.current,
    getMediaStore: state => state.mediaStore,
    getMediaCount: state => state.count,
    getPerPage: state => state.perPage,
    isMediaGalleryOpen: state => state.mediaGallery.open,
    getMediaGalleryConf: state => state.mediaGallery.conf,
  },

  actions: {
    chosenCallback(media: Media) {
      const router = this.router.currentRoute.value
      const { setGameSettingByKey } = useCurrentGameStore()
      const {
        answers,
        setAnswers,
        setCurrentQuestionKey,
        setQuestionByKey,
        question,
      } = useQuestionStore()

      const type = router.query.gallery as string
      const index = Number(router.query.ansIndex)
      const queIndex = Number(router.query.queIndex)

      // answer media
      if (type === 'ansMedia' && index !== undefined) {
        const newAnswers = answers.map((a, i) => {
          if (i === index) {
            a.ansMedia = media
            a.ans = media.src
          }
          return a
        })
        setAnswers(newAnswers)
        //add a window event to notify other components
        window.dispatchEvent(new CustomEvent('answers-media-updated'))
      } else if (type === 'matchingAnswerMedia' && index !== undefined) {
        const newAnswers = answers.map((a, i) => {
          if (i === index) {
            a.ansMedia = media
          }
          return a
        })
        setAnswers(newAnswers)
      } else if (type === 'matchingQuestionMedia' && queIndex !== undefined) {
        const questions = question.questions || []
        const newQuestions = [...questions]
        if (newQuestions[queIndex]) {
          newQuestions[queIndex] = {
            ...newQuestions[queIndex],
            media: media,
          }
        }
        setQuestionByKey('questions', newQuestions)
      } else if (type === 'markSrcMedia') {
        setQuestionByKey(
          type as keyof Question['question'],
          normalizeMarkSrcMedia(media)
        )
      } else if (type === 'backgroundMedia') {
        setQuestionByKey(type as keyof Question['question'], media)
      } else if (type === 'logo') {
        setGameSettingByKey('logo', media)
      } else if (type === 'openMedia' || type === 'endMedia') {
        setCurrentQuestionKey(type, media)
      }
    },
    setGalleryOpen(open: boolean) {
      this.mediaGallery.open = open
    },

    setGalleryConf(conf: MediaGalleryConf) {
      this.mediaGallery.conf = conf
    },

    setMedia(media: Media[]) {
      this.mediaStore = media
    },

    unshiftAndMaybePopMedia(newMedia: Media) {
      this.mediaStore.unshift(newMedia)
      if (this.mediaStore.length > this.perPage) {
        this.mediaStore.pop()
      }
    },

    setMediaCount(count: number) {
      this.count = count
    },

    updateMediaTitle({
      mediaId,
      newTitle,
    }: {
      mediaId: string
      newTitle: string
    }) {
      const media = this.mediaStore.find(m => m._id === mediaId)
      if (media) {
        media.title = newTitle
      }
    },

    deleteMedia(mediaId: string) {
      this.mediaStore = this.mediaStore.filter(m => m._id !== mediaId)
      this.count--
    },

    async indexMedia({
      page = 1,
      perPage = 6,
      search = '',
      type = '',
    }: {
      page?: number
      perPage?: number
      search?: string
      type?: string
    }) {
      try {
        const params = new URLSearchParams()
        params.append('page', page.toString())
        params.append('perPage', perPage.toString())
        params.append('searchText', search)
        params.append('type', type)

        const {
          data: { media, count },
        } = await axios.get(`/media/?${params.toString()}`)

        this.setMediaCount(count)
        this.setMedia(media)

        return media
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },

    async storeMedia(payload: Record<string, any>) {
      try {
        const { data: media } = await axios.post('/media', payload)
        this.unshiftAndMaybePopMedia(media)
        return media
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },

    async updateMediaTitleOnServer({
      mediaId,
      newTitle,
    }: {
      mediaId: string
      newTitle: string
    }) {
      try {
        const { data: updatedMedia } = await axios.patch(`/media/${mediaId}`, {
          title: newTitle,
        })

        this.updateMediaTitle({
          mediaId: updatedMedia._id,
          newTitle: updatedMedia.title,
        })

        return updatedMedia
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },

    async deleteMediaFromServer(mediaId: string) {
      try {
        await axios.delete(`/media/${mediaId}`)
        this.deleteMedia(mediaId)
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },
  },
})
